import React from "react";
import { Dialog, DialogContent } from "./dialog";
import StoreButtons from "./StoreButtons";
import { appDeepLink } from "../../utils/deepLink";

/**
 * DownloadAppPrompt — modal that interrupts club/meeting card clicks on the
 * marketing site. The full card detail lives in the mobile app, so this is
 * the canonical "you need RunCards to see this" surface.
 *
 * Props:
 *  - open          boolean
 *  - onClose       () => void
 *  - subjectLabel  string  e.g. "Coconut Grove Run Club" or "Tonight's run"
 *  - deepLinkType  'meeting'|'club'|'user'  optional — enables "Open in app"
 *  - deepLinkId    string|number            optional — the item id
 *  - kicker        string  optional eyebrow, defaults based on context
 */
export default function DownloadAppPrompt({
  open,
  onClose,
  subjectLabel,
  deepLinkType,
  deepLinkId,
  kicker = "Get RunCards",
}) {
  const appUrl = appDeepLink(deepLinkType, deepLinkId);
  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        // Keep the modal off the viewport edges on portrait phones by
        // overriding the primitive's full-bleed width with a safe-area cap.
        className="rc-download-prompt !w-[calc(100%-2rem)] max-w-md border-0 bg-transparent p-0 shadow-none"
      >
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0c0d10]">
          {/* Ambient brand glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(120,127,154,0.22),transparent_55%),radial-gradient(circle_at_85%_80%,rgba(58,106,76,0.18),transparent_55%)]"
          />

          <div className="relative px-7 py-9 text-center sm:px-9 sm:py-10">
            <p className="mb-3 font-runcards-bold text-[11px] uppercase tracking-[0.32em] text-white/45">
              {kicker}
            </p>
            <h2 className="font-runcards-black text-[clamp(1.75rem,4vw,2.25rem)] leading-[1.05] tracking-[-0.02em] text-white">
              Open in the app.
            </h2>
            {subjectLabel ? (
              <p className="mt-3 font-runcards text-sm text-white/65 sm:text-base">
                See full details for <span className="text-white">{subjectLabel}</span> —
                map, pace, who's going, and one-tap join — in RunCards.
              </p>
            ) : (
              <p className="mt-3 font-runcards text-sm text-white/65 sm:text-base">
                See full details, the map, who's going, and one-tap join — in RunCards.
              </p>
            )}

            {/* If we have a deep link, offer a direct "Open in app" retry for
                users who do have it installed (the auto-attempt can be blocked
                by the browser, e.g. when not triggered by the tap itself). */}
            {appUrl && (
              <a
                href={appUrl}
                className="mt-7 inline-flex w-full items-center justify-center rounded-xl bg-white px-5 py-3 font-runcards-bold text-sm text-black transition-colors hover:bg-white/90"
              >
                Open in app
              </a>
            )}

            <div className={`flex flex-col items-center gap-3 ${appUrl ? "mt-3" : "mt-7"}`}>
              {appUrl && (
                <p className="font-runcards text-[11px] uppercase tracking-[0.22em] text-white/35">
                  Don't have it yet?
                </p>
              )}
              <StoreButtons variant="glass" className="flex-col sm:flex-row" />
            </div>

            <button
              type="button"
              onClick={onClose}
              className="mt-6 font-runcards text-xs uppercase tracking-[0.22em] text-white/40 transition-colors hover:text-white/70"
            >
              Maybe later
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
